import JwtDecode from "jwt-decode";
import { tokenRefreshService } from "./tokenRefresh";
import { TOKEN_KEY } from "../utilities/constants";
import { loginPathFor, navigateTo } from "./nextPath";

/**
 * Authentication Service
 * Handles login, logout, and token management
 * Replaces react-token-auth with custom implementation
 */

/**
 * Store authentication token and initialize refresh service
 */
export const login = (session) => {
	// Store the session token
	localStorage.setItem(TOKEN_KEY, JSON.stringify(session));
	
	// Initialize token refresh service with the new token
	if (session?.access_token) {
		tokenRefreshService.initialize(session.access_token);
	}
	
	return session;
};

/**
 * Clear authentication and stop refresh service.
 * returnHere: send the user back to this page after they log in (ASB-4053).
 * Use it when the session ended or was never there; leave it off for an
 * explicit "Log out", which should land on the dashboard next time.
 */
export const logout = ({ returnHere = false } = {}) => {
	// Clear stored token
	localStorage.removeItem(TOKEN_KEY);
	
	// Clean up token refresh service
	tokenRefreshService.cleanup();
	
	// Redirect to login
	navigateTo(returnHere ? loginPathFor(window.location) : "/login");
};

/**
 * Get current authentication token
 */
export const getToken = () => {
	try {
		const tokenData = localStorage.getItem(TOKEN_KEY);
		if (tokenData) {
			const parsed = JSON.parse(tokenData);
			return parsed.access_token;
		}
	} catch (error) {
		console.error("Failed to get auth token:", error);
	}
	return null;
};

/**
 * Get current session data
 * Returns null if no valid session exists
 */
export const getSession = () => {
	try {
		const tokenData = localStorage.getItem(TOKEN_KEY);
		if (tokenData) {
			const session = JSON.parse(tokenData);
			if (session && session.access_token) {
				return session;
			}
		}
	} catch (error) {
		console.error("Failed to get session:", error);
	}

	// No valid session found - return null
	// Do not call logout() here as it causes infinite loop on login page
	return null;
};

/**
 * The signed-in user's id: the access token's user_id claim, the same value
 * App.js reads for LogRocket and the API reads for ownership checks. Read-only:
 * it decodes the token and verifies nothing. null when there is no session or
 * the token cannot be decoded.
 */
export const getUserId = () => {
	const session = getSession();
	if (!session) return null;
	try {
		const userId = JwtDecode(session.access_token).user_id;
		return userId === undefined ? null : userId;
	} catch (error) {
		return null;
	}
};

// Export individual functions
export default {
	login,
	logout,
	getToken,
	getSession,
	getUserId
};